import { Injectable, inject } from '@angular/core';
import { ApiGatewayService } from '@cropx-web/services/api-gateway/ApiGatewayService';
import {
  ManagementZones,
  VraActivity,
  VraFormats,
  VraMap,
  VraStrategy,
} from '@cropx-web/services/api-gateway/MachineDataApiService';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  Actions,
  State,
  StateContext,
  Store,
  createPropertySelectors,
} from '@ngxs/store';
import { produce } from 'immer';

import { GenerateManagementZones } from '../../state/generate-management-zones.state';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CreateVraMapPage {
  const type = 'CreateVraMapPage';

  export class SetName {
    static readonly type = `[${type}] SetName`;
    constructor(public name: string) {}
  }

  export class SetZoneSource {
    static readonly type = `[${type}] SetZoneSource`;
    constructor(public zoneSource: ZoneSource) {}
  }

  export class SetActivity {
    static readonly type = `[${type}] SetActivity`;
    constructor(public activity: VraActivity) {}
  }

  export class SetStrategy {
    static readonly type = `[${type}] SetStategy`;
    constructor(public strategy: VraStrategy) {}
  }

  export class SetUnit {
    static readonly type = `[${type}] SetUnit`;
    constructor(public unit: string) {}
  }

  export class SetRateForSequence {
    static readonly type = `[${type}] SetRateForSequence`;
    constructor(
      public rate: number,
      public sequence: number,
    ) {}
  }

  export class ClearManagementZones {
    static readonly type = `[${type}] ClearManagementZones`;
    constructor() {}
  }

  export class SetManagementZones {
    static readonly type = `[${type}] SetManagementZones`;
    constructor(public managementZones: ManagementZones) {}
  }

  export class Restore {
    static readonly type = `[${type}] Restore`;
    constructor(public vraMap: VraMap) {}
  }
}

export enum VraSteps {
  Zones = 'management-zones',
  Script = 'script',
  Download = 'download',
}

export enum ZoneSource {
  Generate = 'Generate',
  ManagementZones = 'ManagementZones',
}

export interface CreateVraMapPageModel {
  // generation form values
  name: string;

  // script form values
  activity: VraActivity;
  strategy: VraStrategy;
  unit: string;
  sequenceRates: Record<number, number>;
  vraFormat: VraFormats;

  // Download form values
  downloadScriptLoading: boolean;
  managementZones: ManagementZones;

  vraMap: VraMap;
  manuallySetVraMap: boolean;

  zoneSource: ZoneSource;
}

@State<CreateVraMapPageModel>({
  name: 'createVraMapPage',
  defaults: {
    name: null,
    downloadScriptLoading: false,
    zoneSource: ZoneSource.Generate,

    activity: VraActivity.SEEDING,
    strategy: VraStrategy.CUSTOM,
    unit: '',
    sequenceRates: {},
    vraFormat: null,

    managementZones: null,

    vraMap: null,
    manuallySetVraMap: false,
  },
})
@Injectable()
export class CreateVraMapPageState {
  private store = inject(Store);
  private api = inject(ApiGatewayService);
  private translate = inject(TranslateService);
  private actions$ = inject(Actions);

  @Action(CreateVraMapPage.SetName)
  setName(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetName,
  ): void {
    ctx.patchState({
      name: action.name,
    });
  }

  @Action(CreateVraMapPage.SetZoneSource)
  setZoneSource(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetZoneSource,
  ): void {
    ctx.patchState({
      zoneSource: action.zoneSource,
    });
  }

  @Action(CreateVraMapPage.SetActivity)
  setActivity(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetActivity,
  ): void {
    ctx.patchState({
      activity: action.activity,
    });
  }

  @Action(CreateVraMapPage.SetStrategy)
  setStrategy(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetStrategy,
  ): void {
    ctx.patchState({
      strategy: action.strategy,
    });
  }

  @Action(CreateVraMapPage.SetUnit)
  setUnit(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetUnit,
  ): void {
    ctx.patchState({
      unit: action.unit,
    });
  }

  @Action(CreateVraMapPage.SetRateForSequence)
  setRateForSequence(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetRateForSequence,
  ): void {
    ctx.setState(
      produce((s) => {
        s.sequenceRates[action.sequence] = action.rate;
      }),
    );
  }

  @Action(CreateVraMapPage.SetManagementZones)
  setManagementZones(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.SetManagementZones,
  ): void {
    ctx.setState(
      produce((state) => {
        const sequenceRates = action.managementZones?.legend.reduce(
          (sr, legend) => {
            sr[legend.sequence_nr] = 0;
            return sr;
          },
          {},
        );
        state.managementZones = action.managementZones;
        state.sequenceRates = sequenceRates || {};
      }),
    );
  }

  @Action(CreateVraMapPage.Restore)
  restore(
    ctx: StateContext<CreateVraMapPageModel>,
    action: CreateVraMapPage.Restore,
  ): void {
    ctx
      .dispatch([
        new CreateVraMapPage.SetManagementZones(
          // A vra map is the same as a management zone, but its not actually a management zone, so remove the ID.
          { ...action.vraMap, uuid: undefined },
        ),
        new GenerateManagementZones.Restore(action.vraMap.basedOn),
      ])
      .subscribe(() => {
        ctx.setState(
          produce((state) => {
            state.vraMap = action.vraMap;

            const defaultProduct =
              action.vraMap.script.products.productRates.find(
                (p) => p.productName === undefined,
              );

            state.activity = action.vraMap.script.activity;
            state.strategy = action.vraMap.script.strategy;
            state.unit = defaultProduct.productUnit;
            state.sequenceRates = defaultProduct.rates.reduce((sr, pr) => {
              sr[pr.sequenceNr] = pr.rate;
              return sr;
            }, {});
          }),
        );
      });
  }

  @Action(CreateVraMapPage.ClearManagementZones)
  clearManagementZones(ctx: StateContext<CreateVraMapPageModel>): void {
    ctx.patchState({
      managementZones: null,
      sequenceRates: null,
    });
  }
}

export class CreateVraMapPageSelectors {
  static slices = createPropertySelectors<CreateVraMapPageModel>(
    CreateVraMapPageState,
  );
}
